import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import { useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  heading: {
    id: 'companyProfile.companyFacts.heading',
    defaultMessage: 'Good to know',
    description:
      'Heading of the company profile card summarizing benefits, values, remote policy, offices, and ownership stated on the company\'s own site',
  },
  benefitsLabel: {
    id: 'companyProfile.companyFacts.benefitsLabel',
    defaultMessage: 'Benefits',
    description: 'Label above the employee benefits text on the company facts card',
  },
  valuesLabel: {
    id: 'companyProfile.companyFacts.valuesLabel',
    defaultMessage: 'Values',
    description: 'Label above the company values text on the company facts card',
  },
  remotePolicyLabel: {
    id: 'companyProfile.companyFacts.remotePolicyLabel',
    defaultMessage: 'Remote work',
    description: 'Label above the stated remote/hybrid/on-site policy text on the company facts card',
  },
  officeLocationsLabel: {
    id: 'companyProfile.companyFacts.officeLocationsLabel',
    defaultMessage: 'Offices',
    description: 'Label above the list of office locations on the company facts card',
  },
  corporateHistoryLabel: {
    id: 'companyProfile.companyFacts.corporateHistoryLabel',
    defaultMessage: 'Ownership',
    description: 'Label above the parent company / acquisition history text on the company facts card',
  },
  contactLabel: {
    id: 'companyProfile.companyFacts.contactLabel',
    defaultMessage: 'Contact',
    description: 'Label above the general email/phone contact links on the company facts card',
  },
  teamLabel: {
    id: 'companyProfile.companyFacts.teamLabel',
    defaultMessage: 'Team',
    description: 'Label above the link to the company\'s own team/leadership page on the company facts card',
  },
  teamLinkText: {
    id: 'companyProfile.companyFacts.teamLinkText',
    defaultMessage: 'Meet the team',
    description: 'Text of the link to the company\'s own team/leadership page on the company facts card',
  },
});

interface CompanyFactsCardProps {
  company: Pick<
    AlgoliaCompanyRecord,
    | 'benefits'
    | 'companyValues'
    | 'remotePolicy'
    | 'officeLocations'
    | 'corporateHistory'
    | 'contactEmail'
    | 'contactPhone'
    | 'employeesPageUrl'
  >;
  className?: string;
}

function Fact({ label, text }: { label: string; text?: string }) {
  if (!text) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">{label}</h3>
      <p className="mt-1 text-sm leading-relaxed text-[#1E1A14] dark:text-[#F2EADF]">{text}</p>
    </div>
  );
}

// A general/role-based contact channel (info@..., a switchboard number) — never a named
// individual's own details. See extract-general-contact-info.ts for how this is sourced
// and why it's restricted to that.
function ContactFact({ label, email, phone }: { label: string; email?: string; phone?: string }) {
  if (!email && !phone) return null;
  const linkClassName = 'block text-[#1E1A14] underline-offset-2 hover:underline dark:text-[#F2EADF]';
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">{label}</h3>
      <div className="mt-1 text-sm leading-relaxed">
        {email && (
          <a href={`mailto:${email}`} className={linkClassName}>
            {email}
          </a>
        )}
        {phone && (
          <a href={`tel:${phone}`} className={linkClassName}>
            {phone}
          </a>
        )}
      </div>
    </div>
  );
}

// A bare link out to the company's own team/leadership page — we never fetch, parse, or
// display anything about who's actually on it (see CompanySiteProfile.employeesPageUrl
// for the GDPR reasoning). Opens in a new tab since it navigates away from JobCrawls
// entirely, same as the social links elsewhere on this page.
function TeamLinkFact({ label, linkText, url }: { label: string; linkText: string; url?: string }) {
  if (!url) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">{label}</h3>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-block text-sm leading-relaxed text-[#1E1A14] underline-offset-2 hover:underline dark:text-[#F2EADF]"
      >
        {linkText}
      </a>
    </div>
  );
}

/**
 * Grounded facts pulled from the company's own site (see CompanySiteProfile) — a
 * companion to the About text in the header. Any field can be independently missing;
 * the card itself disappears entirely once none of them are. Citations for this data
 * live once, next to the About text (CompanySiteSources) — not repeated here, since
 * `sources` covers every CompanySiteProfile field together, not this card alone.
 */
export function CompanyFactsCard({ company, className = '' }: CompanyFactsCardProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const {
    benefits,
    companyValues,
    remotePolicy,
    officeLocations,
    corporateHistory,
    contactEmail,
    contactPhone,
    employeesPageUrl,
  } = company;
  if (
    !benefits &&
    !companyValues &&
    !remotePolicy &&
    !officeLocations &&
    !corporateHistory &&
    !contactEmail &&
    !contactPhone &&
    !employeesPageUrl
  ) {
    return null;
  }

  return (
    <div
      className={`rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-4 sm:p-5 ${className}`}
    >
      <h2 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
        {formatMessage(messages.heading)}
      </h2>
      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Fact label={formatMessage(messages.benefitsLabel)} text={benefits} />
        <Fact label={formatMessage(messages.valuesLabel)} text={companyValues} />
        <Fact label={formatMessage(messages.remotePolicyLabel)} text={remotePolicy} />
        <Fact label={formatMessage(messages.officeLocationsLabel)} text={officeLocations} />
        <Fact label={formatMessage(messages.corporateHistoryLabel)} text={corporateHistory} />
        <ContactFact label={formatMessage(messages.contactLabel)} email={contactEmail} phone={contactPhone} />
        <TeamLinkFact
          label={formatMessage(messages.teamLabel)}
          linkText={formatMessage(messages.teamLinkText)}
          url={employeesPageUrl}
        />
      </div>
    </div>
  );
}

CompanyFactsCard.displayName = 'CompanyFactsCard';
